import { useEffect, useId, useRef, useState } from "react";
import { Form, useActionData, useLoaderData, useNavigation, type ActionFunctionArgs, type LoaderFunctionArgs } from "react-router";
import { AvatarPicker } from "../../components/avatar-picker.jsx";
import { translate, useI18n } from "../../i18n/index.js";
import { isApiError } from "../../lib/api.js";
import { authClient } from "../../lib/auth-client.js";
import { apiContext, invalidateSession, sessionContext } from "../../lib/session.js";
import { FormError, Input, PageHeader, SaveBar, SettingsRow, SettingsSection, useFormDirty, useToast, Skeleton } from "../../ui/index.js";

/**
 * Settings: the signed-in person's profile — picture, preferred name, username. The picture is
 * uploaded to user content through control-api; the name and username are Better Auth's, written
 * through its client. The email is read-only: changing a sign-in address needs a verification
 * email this deployment does not send yet.
 */

type ActionResult = { ok?: string; error?: string; field?: "name" | "username" | "image" };
type Profile = { email: string; name: string; username: string; image: string | null };

export function profileLoader({ context }: LoaderFunctionArgs) {
  const actor = context.get(sessionContext)?.actor;
  const profile: Profile = { email: actor?.email ?? "", name: actor?.name ?? "", username: actor?.username ?? "", image: actor?.image ?? null };
  return { profile };
}

export async function profileAction({ request, context }: ActionFunctionArgs) {
  const form = await request.formData();
  // The picture is sent only when it changed; an empty value removes it.
  if (form.has("image")) {
    try {
      await context.get(apiContext).updateAvatar(String(form.get("image")) || null);
    } catch (err) {
      if (isApiError(err, "validation_failed")) return { error: err.message, field: "image" } satisfies ActionResult;
      if (isApiError(err)) return { error: err.message } satisfies ActionResult;
      throw err;
    }
  }
  const username = String(form.get("username") ?? "").trim();
  const { error } = await authClient.updateUser({ name: String(form.get("name") ?? "").trim(), username: username || null });
  if (error) {
    switch (error.code) {
      case "USERNAME_IS_ALREADY_TAKEN":
        return { error: translate("settings.profile.usernameTaken"), field: "username" } satisfies ActionResult;
      case "USERNAME_TOO_SHORT":
      case "USERNAME_TOO_LONG":
      case "INVALID_USERNAME":
        return { error: translate("settings.profile.usernameInvalid"), field: "username" } satisfies ActionResult;
      default:
        return { error: error.message ?? translate("error.requestFailed") } satisfies ActionResult;
    }
  }
  invalidateSession(); // the account menu reads the name and picture from /api/me
  return { ok: translate("settings.profile.saved") } satisfies ActionResult;
}

function ProfileForm({ profile, busy, result }: { profile: Profile; busy: boolean; result: ActionResult | undefined }) {
  const { t } = useI18n();
  const [image, setImage] = useState(profile.image);
  const [unreadable, setUnreadable] = useState(false);
  const nameId = useId();
  const usernameId = useId();
  const form = useFormDirty();
  const imageError = unreadable ? t("avatar.unreadable") : result?.field === "image" ? result.error : null;
  return (
    <Form ref={form.ref} method="post" onInput={form.check} onChange={form.check}>
      <SettingsSection>
        <SettingsRow label={t("settings.profile.avatar")} error={imageError}>
          <AvatarPicker
            name={profile.name || profile.email}
            value={image}
            round
            onChange={(next) => {
              setImage(next);
              setUnreadable(false);
            }}
            onUnreadable={() => setUnreadable(true)}
          />
          {image !== profile.image && <input type="hidden" name="image" value={image ?? ""} />}
        </SettingsRow>
        <SettingsRow label={t("field.email")}>
          {profile.email}
        </SettingsRow>
        <SettingsRow label={t("settings.profile.name")} labelFor={nameId} error={result?.field === "name" ? result.error : null}>
          <Input id={nameId} name="name" defaultValue={profile.name} aria-invalid={result?.field === "name" || undefined} maxLength={100} required />
        </SettingsRow>
        <SettingsRow label={t("settings.profile.username")} labelFor={usernameId} error={result?.field === "username" ? result.error : null}>
          <Input id={usernameId} name="username" defaultValue={profile.username} aria-invalid={result?.field === "username" || undefined} pattern="[A-Za-z0-9_.]{3,30}" autoComplete="username" />
        </SettingsRow>
      </SettingsSection>
      <SaveBar open={form.dirty} message={t("common.unsavedChanges")} saveLabel={t("settings.workspace.save")} busy={busy} />
    </Form>
  );
}

export function ProfileSkeleton() {
  const { t } = useI18n();
  return (
    <>
      <PageHeader title={t("settings.profile.title")} description={t("settings.profile.description")} />
      <SettingsSection>
        <SettingsRow label={t("settings.profile.avatar")}>
          <Skeleton shape="circle" width={40} height={40} />
        </SettingsRow>
        <SettingsRow label={t("field.email")}>
          <Skeleton width={180} />
        </SettingsRow>
        <SettingsRow label={t("settings.profile.name")}>
          <Skeleton shape="input" />
        </SettingsRow>
        <SettingsRow label={t("settings.profile.username")}>
          <Skeleton shape="input" />
        </SettingsRow>
      </SettingsSection>
    </>
  );
}

export function ProfileRoute() {
  const { profile } = useLoaderData<typeof profileLoader>();
  const result = useActionData() as ActionResult | undefined;
  const busy = useNavigation().state === "submitting";
  const { t } = useI18n();
  const toast = useToast();
  const announced = useRef<ActionResult>(undefined);
  useEffect(() => {
    if (!result?.ok || announced.current === result) return;
    announced.current = result;
    toast.success(result.ok);
  }, [result, toast]);
  // Re-keyed on what the server stored, so a save resets the form to it while a rejected one keeps the draft.
  const stored = `${profile.name}\n${profile.username}\n${profile.image?.length ?? 0}:${profile.image?.slice(-24) ?? ""}`;
  return (
    <>
      <PageHeader title={t("settings.profile.title")} description={t("settings.profile.description")} />
      {result?.error && !result.field && <FormError message={result.error} />}
      <ProfileForm key={stored} profile={profile} busy={busy} result={result} />
    </>
  );
}
